@if (!loadingApplication) {
  <div>
    <div id="banner" class="content-block application-overview-header-container">
      <!-- RPO sparkline — absolutely positioned behind header content -->
      <!-- While a navigation refresh is in flight (or before the first load) show a loading state
           so we never render the previous visit's stale chart. -->
      @if (recoveryDataRefreshing || !recoveryPointAttemptsLoaded) {
        <div class="chart-container">
          <!-- Skeleton stands in the chart's exact box — area silhouette, threshold line and
          range pills — so the real chart takes its place without anything shifting. -->
          <div class="chart-loading" role="status" aria-label="Loading RPO chart">
            <div class="chart-skeleton-area"></div>
            <div class="chart-skeleton-threshold"></div>
            <div class="chart-skeleton-pills">
              <div class="chart-skeleton-pill"></div>
              <div class="chart-skeleton-pill"></div>
              <div class="chart-skeleton-pill"></div>
              <div class="chart-skeleton-pill"></div>
            </div>
          </div>
        </div>
      } @else {
        <div class="chart-container">
          @if (hasChartData) {
            <rpo-chart [rpo]="application.rpo" [recoveryPointAttempts]="recoveryPointAttempts"> </rpo-chart>
            <!-- Hidden for now — expand chart feature disabled -->
            <!-- <button
            class="chart-fullscreen-btn"
            (click)="chartFullscreen = true"
            pTooltip="Expand chart"
            tooltipPosition="left"
            aria-label="Expand chart"
            >
            <svg lucideExpand></svg>
          </button> -->
          } @else {
            <div class="chart-no-data">No RPO data yet</div>
          }
        </div>
      }
      <!-- Top row: app name + badge + gear -->
      <div class="header-top-row">
        <div class="header-name-group">
          <h2 class="app-name">{{ application.name }}</h2>
          @switch (application.syncPhase) {
            @case ('failover') {
              <div class="sync-phase-glyph recovery-phase">In Recovery</div>
            }
            @case ('failoverTest') {
              <div class="sync-phase-glyph recovery-test-phase">Recovery Test</div>
            }
            @case ('standby') {
              <div class="sync-phase-glyph standby-phase" data-cy="standby-phase-status">Standby</div>
            }
          }
          <ng-container>
            @if (appIsDeleted) {
              <div class="sync-phase-glyph deleted-app" data-cy="deleting-phase-status">
                @if (refreshing) {
                  <svg lucideLoaderCircle class="lucide-spin"></svg>
                }
                Deleting
              </div>
            }
          </ng-container>
        </div>
        @if (appResourcesLoaded) {
          <div class="header-gear">
            <ng-container>
              @if (!appIsDeleted) {
                <a class="normal-text" [routerLink]="configureLink" aria-label="Configure application">
                  <span class="configure-icon-wrap">
                    <svg lucideSettings class="configure-icon"></svg>
                  </span>
                </a>
              } @else {
                <span class="configure-icon-wrap">
                  <svg lucideSettings class="configure-icon disabled-icon"></svg>
                </span>
              }
            </ng-container>
          </div>
        } @else {
          <div class="header-gear" id="loading-spinner">
            <h1><svg lucideLoaderCircle class="configure-icon lucide-spin"></svg></h1>
          </div>
        }
      </div>
      <!-- Details strip: ACCOUNTS | LAST TEST -->
      <div class="header-details-strip">
        <div class="detail-group detail-accounts">
          <span class="detail-label">Accounts</span>
          <span class="detail-value detail-accounts-value">
            {{ sourceDescription
            }}<a
              class="copy-id-btn"
              [copy-clipboard]="sourceAccountId"
              (copied)="onAccountIdCopied('source')"
              [pTooltip]="application?.isAzure ? 'Copy subscription ID' : 'Copy account ID'"
              tooltipPosition="top"
            >
              @if (copyFeedback.copiedStates['source']) {
                <svg lucideCheck></svg>
              } @else {
                <svg lucideCopy></svg>
              }
            </a>
            &rarr; {{ targetDescription
            }}<a
              class="copy-id-btn"
              [copy-clipboard]="targetAccountId"
              (copied)="onAccountIdCopied('target')"
              [pTooltip]="application?.isAzure ? 'Copy subscription ID' : 'Copy account ID'"
              tooltipPosition="top"
            >
              @if (copyFeedback.copiedStates['target']) {
                <svg lucideCheck></svg>
              } @else {
                <svg lucideCopy></svg>
              }
            </a>
          </span>
        </div>
        <div class="detail-group">
          <span class="detail-label">{{ lastTestLabel }}</span>
          <span class="detail-value last-test-value"
            >{{ lastTestValue }}
            @if (lastTestFailed) {
              <svg
                lucideCircleAlert
                class="last-test-failed-icon"
                pTooltip="Your last test was not successful, consider planning a retest soon."
                tooltipPosition="top"
              ></svg>
            }
          </span>
        </div>
        @if (recoveryPointAttemptsLoaded && hasChartData) {
          <a class="mobile-chart-link" href="javascript:void(0)" role="button" (click)="chartFullscreen = true">
            View RPO chart
          </a>
        }
      </div>
    </div>
    <div
      class="content-block light-control-host application-dashboard-body"
      arpObjectErrorHost="application-overview"
      #overviewError="objectErrorHost"
      [errorHostActions]="overviewErrorActions"
    >
      <!-- Main tabs + action buttons on same row -->
      <div class="overview-tabs-bar" role="tablist" aria-label="Application overview tabs">
        <button
          type="button"
          class="overview-tab"
          role="tab"
          [attr.aria-selected]="activeTab === Tab.BACKUP"
          aria-controls="backup-tab-panel"
          [ngClass]="{ active: activeTab === Tab.BACKUP }"
          (click)="showBackupsTab()"
        >
          <svg lucideCloudUpload class="mobile-tab-icon"></svg>
          <span class="tab-label">Backup</span>
          @if (application?.backupIssues?.length > 0 || application?.scanIssues?.length > 0) {
            <svg lucideCloudAlert class="tab-warn-icon"></svg>
          }
        </button>
        <button
          type="button"
          class="overview-tab"
          role="tab"
          [attr.aria-selected]="activeTab === Tab.RECOVERY_ENVIRONMENT || activeTab === Tab.FAILBACK"
          aria-controls="recovery-tab-panel"
          [ngClass]="{ active: activeTab === Tab.RECOVERY_ENVIRONMENT || activeTab === Tab.FAILBACK }"
          (click)="showRecoveryEnvironmentTab()"
        >
          <svg lucideCloudDownload class="mobile-tab-icon"></svg>
          <span class="tab-label">Recovery Environment</span>
          @if (application?.restoreIssues?.length > 0) {
            <svg lucideCircleAlert class="tab-warn-icon"></svg>
          }
        </button>
        @if (hasIssues || activeTab === Tab.ISSUES) {
          <button
            type="button"
            class="overview-tab issues-tab"
            role="tab"
            [attr.aria-selected]="activeTab === Tab.ISSUES"
            aria-controls="issues-tab-panel"
            [ngClass]="{ active: activeTab === Tab.ISSUES }"
            (click)="showIssuesTab()"
          >
            <svg lucideCircleAlert class="mobile-tab-icon"></svg>
            @if (totalIssueCount > 0) {
              <span class="tab-label">All Issues</span> <span class="tab-count">{{ totalIssueCount }}</span>
            } @else {
              <span class="tab-label">All Issues</span>
              <span class="tab-count"><svg lucideLoaderCircle class="lucide-spin"></svg></span>
            }
            <!-- Spinner while issues haven't loaded yet; tab auto-hides once loaded with zero issues -->
          </button>
        }
        <!-- Action buttons pinned to end of tab bar, persist across all tabs.
             Per-phase buttons stay rendered even during in-flight operations
             (failback, restore) — `disabledActionReason` disables them with a
             tooltip rather than swapping the whole row for a bare spinner. -->
        <div class="tab-bar-actions">
          @if (showSyncActions) {
            @if (showSandboxSettingsButton) {
              <button
                type="button"
                id="sandbox-config-button"
                class="arpio-secondary-button"
                [disabled]="hasDisabledActionReason"
                [pTooltip]="disabledActionReason"
                tooltipPosition="top"
                (click)="sandboxConfigButtonClick()"
              >
                Sandbox Settings
              </button>
            }
            @switch (applicationSyncPhase) {
              @case ('failoverTest') {
                @if (canTest) {
                  <button
                    type="button"
                    id="conclude-test-button"
                    data-cy="conclude-test-button"
                    class="arpio-button"
                    [disabled]="hasDisabledActionReason"
                    [pTooltip]="disabledActionReason"
                    tooltipPosition="top"
                    (click)="concludeTestButtonClick()"
                  >
                    {{ isEarlyStopTest ? 'Cancel Test' : 'Conclude Test' }}
                  </button>
                }
              }
              @case ('failover') {
                @if (resourcesAreQuarantined && !appIsDeleted) {
                  <button
                    type="button"
                    id="unquarantine-button"
                    class="arpio-button"
                    [disabled]="hasDisabledActionReason"
                    [pTooltip]="disabledActionReason"
                    tooltipPosition="top"
                    (click)="unquarantineButtonClick()"
                  >
                    Unquarantine
                  </button>
                }
                @if (failbackEnabled && !resourcesAreQuarantined && !appIsDeleted && canRecover) {
                  <button
                    type="button"
                    id="failback-button"
                    class="arpio-button"
                    [disabled]="!hasFailbackResources || hasDisabledActionReason"
                    [pTooltip]="disabledActionReason || failbackButtonDisabledTooltip"
                    tooltipPosition="top"
                    (click)="failbackButtonClick()"
                  >
                    Failback
                  </button>
                }
                @if (!appIsDeleted && canRecover) {
                  <button
                    type="button"
                    id="conclude-recovery-button"
                    data-cy="conclude-recovery-button"
                    class="arpio-danger-button"
                    [disabled]="hasDisabledActionReason"
                    [pTooltip]="disabledActionReason"
                    tooltipPosition="top"
                    (click)="concludeRecoveryButtonClick()"
                  >
                    Conclude Recovery
                  </button>
                }
              }
              @case ('standby') {
                @if (!appIsDeleted) {
                  @if (canTest) {
                    <button
                      type="button"
                      id="test-button"
                      data-cy="test-button"
                      class="arpio-button"
                      [disabled]="!lastStagingSuccess || hasDisabledActionReason"
                      [pTooltip]="disabledActionReason"
                      tooltipPosition="top"
                      (click)="launchTestButtonClick()"
                    >
                      <svg lucideFlaskConical class="mobile-icon"></svg>
                      <span class="button-label">Test</span>
                    </button>
                  }
                  @if (canRecover) {
                    <button
                      type="button"
                      id="failover-button"
                      data-cy="failover-button"
                      class="arpio-danger-button"
                      [disabled]="!lastStagingSuccess || hasDisabledActionReason"
                      [pTooltip]="disabledActionReason"
                      tooltipPosition="top"
                      (click)="recoverButtonClick()"
                    >
                      <svg lucideRocket class="mobile-icon"></svg>
                      <span class="button-label">Launch Recovery</span>
                    </button>
                  }
                }
              }
            }
          }
        </div>
      </div>
      <!-- A state conflict belongs to the application, not to the dialog that hit it: the
           condition outlives the click and the launch controls above disable for the same
           reason. -->
      @if (overviewError.error) {
        <arp-error-block
          class="overview-error"
          [error]="overviewError.error"
          [dismissible]="true"
          (actionInvoked)="onOverviewErrorAction($event)"
          (dismissed)="overviewError.clear()"
        ></arp-error-block>
      }
      <!-- Shared status bar template -->
      <!-- Rich tooltip body for the real-time RPO info icon on the backup status bar.
  [autoHide]=false + [hideDelay]=200 on the host icon lets the cursor traverse
  into this template to click the docs link. -->
      <ng-template #realTimeBackupTooltip>
        <div class="real-time-rpo-tooltip-body">
          This application has realtime replication enabled. Real-time replication works for DynamoDB tables, EC2
          instances, S3 buckets, and RDS DB clusters. Other resources will be replicated via frequent snapshots.
          <a [href]="rtReplicationDocsUrl | safeUrl" target="_blank" class="real-time-rpo-tooltip-link">
            Read more about real-time replication
          </a>
        </div>
      </ng-template>
      <ng-template #statusBarTemplate let-status="status" let-filterType="filterType">
        @if (status) {
          <div
            class="tab-status-bar"
            [ngClass]="status?.state"
            [attr.data-cy]="filterType + '-status-bar'"
            aria-live="polite"
          >
            @if (status.state === 'indeterminate') {
              <svg
                lucideLoaderCircle
                class="tab-status-icon lucide-spin"
                [attr.data-cy]="filterType + '-status-spinner'"
              ></svg>
            }
            <div class="tab-status-content">
              <span class="tab-status-text">
                @if (status.failedLink) {
                  {{ status.failedLink.prefix }}
                  <!-- Pluralize inside the interpolation, not via a separate trailing-"s" element.
                       Putting the plural "s" in its own element leaves template whitespace between
                       the word and the "s" (rendered as "resource s"), and Prettier reformats any
                       single-line workaround back into that whitespace-introducing layout.
                       Keep the anchor boundaries butted against the text so no space is rendered. -->
                  <a
                    class="failed-resource-link"
                    href="javascript:void(0)"
                    role="button"
                    (click)="filterToFailedResources(filterType)"
                    >{{ status.failedLink.count }} {{ status.failedLink.count === 1 ? 'resource' : 'resources' }}</a
                  >
                  {{ status.failedLink.suffix }}
                } @else {
                  {{ status.text }}
                }
              </span>
              @if (status.issues.length > 0) {
                <!-- Pluralize inside the interpolation, not via a separate trailing-"s" element
                     (see the whitespace/Prettier pitfall note on the failed-resource link above). -->
                <a
                  class="tab-status-issues-link"
                  href="javascript:void(0)"
                  role="button"
                  (click)="showIssuesTab(filterType)"
                  >Review and fix {{ status.issues.length }} {{ status.issues.length === 1 ? 'issue' : 'issues' }}</a
                >
              }
              @if (status.progressTotal > 0) {
                <span class="tab-status-progress">
                  <span class="progress-track">
                    <span
                      class="progress-fill"
                      [style.width]="
                        status.progressCount >= status.progressTotal
                          ? '100%'
                          : 'min(' + (status.progressCount / status.progressTotal) * 100 + '%, calc(100% - 3px))'
                      "
                    ></span>
                  </span>
                  <span class="progress-label">({{ status.progressCount }} / {{ status.progressTotal }})</span>
                  <!--
              Real-time RPO: the backend schedules a fresh backup as soon as the last one finishes,
              so "Creating backup..." is the steady state for these apps. The info icon explains
              this so users don't read the perpetual activity as an error.
              -->
                  @if (filterType === 'backup' && isRealTimeRpo) {
                    <svg
                      lucideInfo
                      class="tab-status-info-icon"
                      [pTooltip]="realTimeBackupTooltip"
                      tooltipPosition="top"
                      tooltipStyleClass="real-time-rpo-tooltip"
                      tooltipEvent="hover"
                      [autoHide]="false"
                      [hideDelay]="200"
                      aria-label="About real-time RPO backups"
                    ></svg>
                  }
                </span>
              }
              <!-- Substatus row: upstream-failure hint + progress ratio share one line. -->
              @if (upstreamBlockedCountFor(filterType); as count) {
                <div class="tab-status-substatus">
                  <span class="tab-status-ratio tab-status-upstream">
                    {{ count }}
                    {{ count === 1 ? 'resource was' : 'resources were' }} not processed due to upstream failures.
                  </span>
                  @if (status.ratioText) {
                    <span class="tab-status-ratio tab-status-ratio--inline">{{ status.ratioText }}</span>
                  }
                </div>
              } @else {
                @if (status.ratioText) {
                  <span class="tab-status-ratio">{{ status.ratioText }}</span>
                }
              }
              @if (status.priorBackupInfo) {
                <span class="tab-status-prior-info">{{ status.priorBackupInfo }}</span>
              }
            </div>
          </div>
        }
      </ng-template>
      <!-- subTabHeading removed: ng-content projection requires real DOM children, not ngTemplateOutlet.
  The tab heading markup is inlined directly inside each app-resources-view below. -->
      <!-- Below the tabs, above the per-tab status. Reassures users the view keeps itself current
      automatically, and offers a "refresh now" soft in-app reload (skeleton rows + fresh fetch)
      that recovers a stuck/stale view without signing out — steering them to stay in the console. -->
      <div class="overview-refresh-bar">
        <div class="auto-refresh-indicator">
          @if (manualRefreshing) {
            <span class="refreshing-text"><svg lucideRefreshCw class="lucide-spin"></svg></span>
          } @else {
            <a class="reload-now-link" role="button" tabindex="0" (click)="reloadView()" (keydown.enter)="reloadView()"
              ><svg lucideRefreshCw></svg
            ></a>
          }
          <span class="auto-refresh-text">Automatically updates every {{ autoRefreshSeconds }}s </span>
        </div>
      </div>
      <!-- Recovery Environment Tab Content -->
      @if (activeTab === Tab.RECOVERY_ENVIRONMENT || activeTab === Tab.FAILBACK) {
        <div class="recovery-tab-content" id="recovery-tab-panel" role="tabpanel" aria-label="Recovery Environment">
          <!-- Tab-specific Status Bar: Recovery Environment -->
          @if (activeTab === Tab.RECOVERY_ENVIRONMENT) {
            @if (_recoveryEnvStatus) {
              <ng-container
                [ngTemplateOutlet]="statusBarTemplate"
                [ngTemplateOutletContext]="{ status: _recoveryEnvStatus, filterType: 'recovery' }"
              ></ng-container>
            } @else {
              <div class="tab-status-bar indeterminate" data-cy="recovery-status-bar">
                <svg lucideLoaderCircle class="tab-status-icon lucide-spin" data-cy="recovery-status-spinner"></svg>
                <span class="tab-status-text">Loading status...</span>
              </div>
            }
          }
          <!-- Tab-specific Status Bar: Failback -->
          @if (activeTab === Tab.FAILBACK && _failbackStatus) {
            <ng-container
              [ngTemplateOutlet]="statusBarTemplate"
              [ngTemplateOutletContext]="{ status: _failbackStatus, filterType: 'failback' }"
            ></ng-container>
          }
          <!-- Recovery environment operation info notice — only on the recovery sub-tab.
      Click anywhere on the notice to toggle expanded/collapsed. Inner interactive
      elements (links, nested allowlist toggles, the chevron button) stopPropagation
      so their own handlers run without also flipping the accordion. -->
          @if (activeTab === Tab.RECOVERY_ENVIRONMENT && !isStandby) {
            <div
              class="operation-status-notice"
              [class.operation-status-notice--collapsed]="operationNoticeCollapsed"
              [class.operation-status-notice--static]="!operationNoticeHasDetails"
              [attr.role]="operationNoticeHasDetails ? 'button' : null"
              [attr.aria-expanded]="operationNoticeHasDetails ? !operationNoticeCollapsed : null"
              [attr.tabindex]="operationNoticeHasDetails ? 0 : null"
              (click)="operationNoticeHasDetails && (operationNoticeCollapsed = !operationNoticeCollapsed)"
              (keydown.enter)="operationNoticeHasDetails && (operationNoticeCollapsed = !operationNoticeCollapsed)"
              (keydown.space)="
                operationNoticeHasDetails && (operationNoticeCollapsed = !operationNoticeCollapsed);
                $event.preventDefault()
              "
            >
              <svg lucideInfo></svg>
              <div class="operation-status-content">
                <!-- First line: always visible (recovery point + sandbox summary) -->
                <span class="operation-status-summary">
                  Applied recovery point is {{ currentRecoveryPointTimeString }}.
                  @if (showNetworkSandboxStatus) {
                    Network sandbox is {{ networkSandboxStatus }}.
                    @if (
                      application.networkSandboxEnabled &&
                      application.advancedNetworkSandboxEnabled &&
                      applicationSyncPhase == 'failoverTest'
                    ) {
                      <a
                        href="javascript:void(0)"
                        role="button"
                        (click)="showSandboxEventsDialog(); $event.stopPropagation()"
                        >See most recent events.</a
                      >
                    }
                  }
                  @if (showRansomwareRecoveryStatus) {
                    Quarantine Mode is {{ ransomwareRecoveryStatus }}.
                  }
                </span>
                <!-- Detail lines: domains + CIDRs (hidden when collapsed) -->
                @if (!operationNoticeCollapsed) {
                  @if (
                    showNetworkSandboxStatus &&
                    application.advancedNetworkSandboxEnabled &&
                    application.networkSandboxAllowedDomains?.length
                  ) {
                    <div
                      class="allowlist-status"
                      [class.collapsed]="!domainAllowlistExpanded"
                      [class.truncated]="domainAllowlistTruncated"
                      (mouseenter)="checkAllowlistTruncation('domain', $event)"
                      (click)="
                        domainAllowlistTruncated && (domainAllowlistExpanded = !domainAllowlistExpanded);
                        $event.stopPropagation()
                      "
                    >
                      <span class="allowlist-text">{{ domainAllowlistStatus }}</span>
                      <span class="expand-toggle">{{
                        domainAllowlistExpanded ? 'click to collapse' : 'click to expand'
                      }}</span>
                    </div>
                  }
                  @if (
                    showNetworkSandboxStatus &&
                    accountService.networkSandboxWhitelistEnabled &&
                    application.networkSandboxWhitelist?.length
                  ) {
                    <div
                      class="allowlist-status"
                      [class.collapsed]="!whitelistExpanded"
                      [class.truncated]="whitelistTruncated"
                      (mouseenter)="checkAllowlistTruncation('whitelist', $event)"
                      (click)="whitelistTruncated && (whitelistExpanded = !whitelistExpanded); $event.stopPropagation()"
                    >
                      <span class="allowlist-text">{{ whitelistStatus }}</span>
                      <span class="expand-toggle">{{
                        whitelistExpanded ? 'click to collapse' : 'click to expand'
                      }}</span>
                    </div>
                  }
                }
              </div>
              <!-- Chevron indicator — hidden when there are no expandable details. -->
              @if (operationNoticeHasDetails) {
                <span
                  class="operation-notice-toggle"
                  [pTooltip]="operationNoticeCollapsed ? 'Show details' : 'Hide details'"
                  tooltipPosition="left"
                  aria-hidden="true"
                >
                  @if (operationNoticeCollapsed) {
                    <svg lucideChevronDown></svg>
                  } @else {
                    <svg lucideChevronUp></svg>
                  }
                </span>
              }
            </div>
          }
          <!-- Failback operation info notice — state-aware text via getter, mirroring the
        Recovery tab's pattern of a persistent-but-accurate notice. Hidden when the
        status bar already covers the state (idle with no resources, loading). -->
          @if (activeTab === Tab.FAILBACK && failbackOperationNoticeText) {
            <div class="operation-status-notice operation-status-notice--static">
              <svg lucideInfo></svg>
              <div class="operation-status-content">{{ failbackOperationNoticeText }}</div>
            </div>
          }
          @if (activeTab === Tab.RECOVERY_ENVIRONMENT) {
            <app-resources-view
              #recoveryResourcesView
              [tabType]="Tab.RECOVERY_ENVIRONMENT"
              [appStatus]="lastRestoreStatus"
              [loading]="!application.isScopeLoaded('restore') || restoreLaunching"
              [resources]="
                restoreLaunching && cachedRecoveryResources?.length === 0
                  ? skeletonPlaceholderRows
                  : cachedRecoveryResources
              "
              [application]="application"
              [appIsDeleted]="appIsDeleted"
              [initialFilterText]="filterText"
              [statusContext]="StatusContext.RECOVERY"
              (filterTextChange)="doFilterResources($event)"
              (issuesClicked)="onResourceIssuesClicked($event)"
            >
              <!-- Recovery/Failback sub-tabs — inlined for ng-content projection.
            Uses [hidden] (not @if) so .tab-heading always exists for ng-content select. -->
              <div class="tab-heading" [hidden]="!showFailback">
                <div class="tab">
                  <button
                    type="button"
                    id="recovery-tab"
                    [ngClass]="{ active: activeTab === Tab.RECOVERY_ENVIRONMENT }"
                    (click)="showRecoveryEnvironmentTab()"
                  >
                    <h3 [ngClass]="{ inactive: activeTab !== Tab.RECOVERY_ENVIRONMENT }">Recovery</h3>
                  </button>
                  <button type="button" [ngClass]="{ active: activeTab === Tab.FAILBACK }" (click)="showFailbackTab()">
                    <h3 [ngClass]="{ inactive: activeTab !== Tab.FAILBACK }">Failback</h3>
                  </button>
                </div>
              </div>
            </app-resources-view>
          } @else {
            <app-resources-view
              #failbackResourcesView
              [tabType]="Tab.FAILBACK"
              [appStatus]="lastFailback"
              [loading]="!application.isScopeLoaded('failback')"
              [resources]="cachedFailbackResources"
              [application]="application"
              [appIsDeleted]="appIsDeleted"
              [initialFilterText]="filterText"
              [statusContext]="StatusContext.RECOVERY"
              (filterTextChange)="doFilterResources($event)"
              (issuesClicked)="onResourceIssuesClicked($event)"
            >
              <!-- Recovery/Failback sub-tabs — duplicated for failback view's ng-content projection.
            Uses [hidden] (not @if) so ng-content select=".tab-heading" can always find the element. -->
              <div class="tab-heading" [hidden]="!showFailback">
                <div class="tab">
                  <button
                    type="button"
                    id="recovery-tab"
                    [ngClass]="{ active: activeTab === Tab.RECOVERY_ENVIRONMENT }"
                    (click)="showRecoveryEnvironmentTab()"
                  >
                    <h3 [ngClass]="{ inactive: activeTab !== Tab.RECOVERY_ENVIRONMENT }">Recovery</h3>
                  </button>
                  <button type="button" [ngClass]="{ active: activeTab === Tab.FAILBACK }" (click)="showFailbackTab()">
                    <h3 [ngClass]="{ inactive: activeTab !== Tab.FAILBACK }">Failback</h3>
                  </button>
                </div>
              </div>
            </app-resources-view>
          }
        </div>
      }
      <!-- Backups Tab Content -->
      @if (activeTab === Tab.BACKUP) {
        <div class="backup-tab-content" id="backup-tab-panel" role="tabpanel" aria-label="Backup">
          <!-- Backup Health Status Bar -->
          @if (_backupHealthStatus) {
            <ng-container
              [ngTemplateOutlet]="statusBarTemplate"
              [ngTemplateOutletContext]="{ status: _backupHealthStatus, filterType: 'backup' }"
            ></ng-container>
          } @else {
            <div class="tab-status-bar indeterminate" data-cy="backup-status-bar">
              <svg lucideLoaderCircle class="tab-status-icon lucide-spin" data-cy="backup-status-spinner"></svg>
              <span class="tab-status-text">Loading status...</span>
            </div>
          }
          <app-resources-view
            #backupResourcesView
            [tabType]="Tab.BACKUP"
            [appStatus]="lastBackupStatus"
            [loading]="!application.isScopeLoaded('backup')"
            [resources]="cachedBackupResources"
            [application]="application"
            [appIsDeleted]="appIsDeleted"
            [initialFilterText]="filterText"
            [statusContext]="StatusContext.BACKUP"
            (filterTextChange)="doFilterResources($event)"
            (issuesClicked)="onResourceIssuesClicked($event)"
          >
          </app-resources-view>
        </div>
      }
      <!-- Issues Tab Content -->
      @if (activeTab === Tab.ISSUES) {
        <div class="issues-tab-content" id="issues-tab-panel" role="tabpanel" aria-label="Issues">
          <!-- Loading state: hold the spinner until issues load AND their resource
          enrichment is ready, so grouping runs once and issues don't flash ungrouped
          then snap into groups. Tab auto-redirects once confirmed empty. -->
          @if (!issuesReadyToRender) {
            <!-- Placeholders reuse the loaded layout's own containers (toolbar, divider, section
            header) so the real content drops into the same geometry without the page jumping. -->
            <div class="issues-loading" role="status" aria-label="Loading issues">
              <div class="issues-tab-toolbar">
                <div class="issues-skeleton-description"></div>
                <div class="issues-skeleton-icon-button"></div>
              </div>
              <hr class="issues-section-divider-line" />
              <div class="issues-section-divider">
                <div class="issues-skeleton-section-label"></div>
                <div class="issues-skeleton-action-button"></div>
              </div>
              <div class="issue-skeleton-row">
                <div class="issue-skeleton-title"></div>
                <div class="issue-skeleton-resource"></div>
              </div>
            </div>
          } @else {
            <!-- Top-right export button — exports every issue across all scopes (backup +
        recovery) as CSV. Uses the same arpio-secondary-button styling as the per-tab export controls. -->
            <div class="issues-tab-toolbar">
              <div class="issues-tab-description">Review and fix the following issues:</div>
              <button
                type="button"
                class="export-all-issues-btn arpio-secondary-button extra-small-button icon-only-button"
                (click)="exportAllIssuesToCsv()"
                pTooltip="Export all issues to CSV"
                tooltipPosition="top"
              >
                <svg lucideUpload></svg>
              </button>
            </div>
            <hr class="issues-section-divider-line" />
            <!-- Backup Issues section -->
            @if (backupIssues.length > 0) {
              <div class="issues-section" id="issues-section-backup">
                <div class="issues-section-divider">
                  <h3 class="section-label">Backup Issues</h3>
                  @if (accountService.entitledForCapability('backup') && canManage) {
                    <button
                      type="button"
                      busy-button
                      [busy]="backingUp"
                      [disabled]="backingUp"
                      id="retry-backup-button"
                      class="arpio-button small-button"
                      (click)="launchBackupToTryAgain()"
                    >
                      <svg lucideRotateCw></svg>
                      Retry Backup
                    </button>
                  }
                </div>
                <issues-view
                  [sync-pair]="application.syncPair"
                  [application]="application"
                  [issues]="backupIssues"
                  (resourceClicked)="navigateToResourceInTab('backup', $event)"
                ></issues-view>
              </div>
            }
            @if (backupIssues.length > 0 && restoreIssues.length > 0) {
              <hr class="issues-section-divider-line" />
            }
            <!-- Recovery Environment Issues section -->
            @if (restoreIssues.length > 0) {
              <div class="issues-section" id="issues-section-recovery">
                <div class="issues-section-divider">
                  <h3 class="section-label">Recovery Environment Issues</h3>
                  @if (accountService.entitledForCapability('restore') && _cachedHasRestorePermission) {
                    @if (canTryAgainFromJob) {
                      @if (!restoring) {
                        <!-- Lowercase tag name: the camelCase p-splitButton selector alias was dropped by PrimeNG,
                             which silently renders nothing. p-splitbutton is the alias supported across versions. -->
                        <p-splitbutton
                          label="Resume Restore"
                          id="resume-recovery-split-button"
                          [model]="restoreMenuItems"
                          (onClick)="launchRestoreToTryAgain(true)"
                        >
                          <!-- The icon/dropdownIcon inputs render a class-driven <i>, which Lucide can't fill.
                               Projecting both through templates is the only way to get the svgs in. -->
                          <ng-template #content>
                            <svg lucideRotateCw></svg>
                            Resume Restore
                          </ng-template>
                          <ng-template #dropdownicon>
                            <svg lucideEllipsisVertical></svg>
                          </ng-template>
                        </p-splitbutton>
                      } @else {
                        <button
                          type="button"
                          busy-button
                          [busy]="true"
                          disabled
                          class="arpio-button small-button"
                          id="resume-recovery-button"
                        >
                          Resume Restore
                        </button>
                      }
                    } @else {
                      <button
                        type="button"
                        busy-button
                        [busy]="restoring"
                        [disabled]="restoring"
                        id="retry-recovery-button"
                        class="arpio-button small-button"
                        (click)="launchRestoreToTryAgain(false)"
                      >
                        <svg lucideRotateCw></svg>
                        Retry Restore
                      </button>
                    }
                  }
                </div>
                <issues-view
                  [sync-pair]="application.syncPair"
                  [application]="application"
                  [issues]="restoreIssues"
                  (resourceClicked)="navigateToResourceInTab('recovery', $event)"
                ></issues-view>
              </div>
            }
            <!-- Failback Issues section -->
            @if (failbackEnabled && failbackIssues.length > 0) {
              <div class="issues-section" id="issues-section-failback">
                <div class="issues-section-divider">
                  <h3 class="section-label">Failback Issues</h3>
                </div>
                <issues-view
                  [sync-pair]="application.syncPair"
                  [application]="application"
                  [issues]="failbackIssues"
                  (resourceClicked)="navigateToResourceInTab('failback', $event)"
                ></issues-view>
              </div>
            }
          }
        </div>
      }
    </div>
  </div>
} @else {
  <h1 id="application-loading" class="content-block"><svg lucideLoaderCircle class="lucide-spin"></svg> Loading</h1>
}

<modal-dialog #failoverDialog class="wide-modal" [dialogTemplate]="failoverDialogTemplate"></modal-dialog>

<ng-template #failoverDialogTemplate>
  <div class="flex-center-column full-width">
    <div class="dialog light-content-panel">
      @if (!showAreYouSure) {
        <launch-recovery-dialog
          [launching]="launchingSync"
          [application]="application"
          (cancel)="dismissModal()"
          (launch)="onLaunchRecovery($event)"
        >
        </launch-recovery-dialog>
      }

      @if (showAreYouSure) {
        <div class="flex-center-column full-width light-control-host">
          <div class="">
            <h3>Are you sure you want to launch recovery for the following applications?</h3>
            @if (quarantineEnabled) {
              <div class="margin-top font-bold">Quarantine Mode: ON</div>
            } @else {
              <div class="margin-top font-bold">Quarantine Mode: OFF</div>
            }
            <ul>
              @for (app of selectedApplications; track app) {
                <li>{{ app.name }}</li>
              }
            </ul>
            <div class="notice-block indeterminate">
              Launching a Recovery will restore your application in your recovery environment without the safeguards of
              a Test.
              <a href="{{ uiService.testVsRecoveryFaq }}" target="_blank">Learn more.</a>
            </div>
          </div>
          <div class="flex-center-row justify-center full-width margin-top">
            <button type="button" class="arpio-secondary-button" (click)="onCancelRecoveryConfirmation()">
              No, Cancel Recovery
            </button>
            <button
              type="button"
              class="arpio-danger-button text-white margin-left"
              data-cy="confirm-yes-button"
              (click)="confirmLaunchRecovery()"
            >
              Yes, I'm Sure
            </button>
          </div>
        </div>
      }
    </div>
  </div>
</ng-template>

<modal-dialog
  #concludeFailoverDialog
  class="wide-modal"
  [dialogTemplate]="concludeFailoverDialogTemplate"
></modal-dialog>

<ng-template #concludeFailoverDialogTemplate>
  <div class="flex-center-column">
    <div class="dialog light-content-panel">
      <conclude-recovery-dialog
        [launching]="launchingSync"
        [application]="application"
        (cancel)="dismissConcludeModal()"
        [currentlySyncing]="application.currentlyRestoring && !launchingSync"
        (conclude)="concludeRecovery($event)"
      >
      </conclude-recovery-dialog>
    </div>
  </div>
</ng-template>

<modal-dialog #testFailoverDialog class="wide-modal" [dialogTemplate]="testFailoverDialogTemplate"></modal-dialog>

<ng-template #testFailoverDialogTemplate>
  <div class="flex-center-column full-width">
    <div class="dialog light-content-panel">
      <launch-test-dialog
        [launching]="launchingSync"
        [application]="application"
        [currentlySyncing]="application.currentlyRestoring && !launchingSync"
        (cancel)="dismissLaunchTestModal()"
        (launch)="launchTest($event)"
      >
      </launch-test-dialog>
    </div>
  </div>
</ng-template>

<modal-dialog #sandboxConfigDialog [dialogTemplate]="sandboxConfigDialogTemplate"></modal-dialog>

<ng-template #sandboxConfigDialogTemplate>
  <div class="flex-center-column full-width">
    <div class="dialog test-config-dialog light-content-panel">
      <application-test-config-dialog
        [launching]="launchingSync"
        [application]="application"
        [currentlySyncing]="application.currentlyRestoring && !launchingSync"
        (cancel)="dismissSandboxConfigModal()"
        (launch)="launchTest($event)"
      >
      </application-test-config-dialog>
    </div>
  </div>
</ng-template>

<modal-dialog
  #concludeTestFailoverDialog
  class="wide-modal"
  [dialogTemplate]="concludeTestFailoverDialogTemplate"
></modal-dialog>

<ng-template #concludeTestFailoverDialogTemplate>
  <div class="flex-center-column full-width">
    <div class="dialog light-content-panel">
      <conclude-test-dialog
        [launching]="launchingSync"
        [application]="application"
        (cancel)="dismissConcludeTestModal()"
        (conclude)="concludeTestFailover($event)"
      ></conclude-test-dialog>
    </div>
  </div>
</ng-template>

<modal-dialog #failbackDialog [dialogTemplate]="failbackDialogTemplate"></modal-dialog>

<ng-template #failbackDialogTemplate>
  <div class="flex-center-column full-width">
    <div class="failback-dialog light-content-panel">
      <failback-dialog
        [application]="application"
        [launching]="launchingFailback"
        (failback)="launchFailback($event)"
        (cancel)="dismissFailbackModal()"
      ></failback-dialog>
    </div>
  </div>
</ng-template>

<modal-dialog #unquarantineDialog [dialogTemplate]="unquarantineDialogTemplate"></modal-dialog>

<ng-template #unquarantineDialogTemplate>
  <div class="flex-center-column full-width">
    <div class="dialog light-content-panel">
      <unquarantine-dialog [application]="application" (close)="dismissUnquarantineModal()"></unquarantine-dialog>
    </div>
  </div>
</ng-template>

<modal-dialog #awsEventLogsDialog [dialogTemplate]="awsEventLogsDialogTemplate"></modal-dialog>

<ng-template #awsEventLogsDialogTemplate>
  <app-aws-event-logs
    [accountId]="accountService.accountId"
    [applicationId]="applicationId"
    (close)="awsEventLogsDialog.close()"
  ></app-aws-event-logs>
</ng-template>

<!-- RPO chart fullscreen overlay — tabindex + keydown.escape enable keyboard dismissal,
focus is auto-moved to close button when overlay opens (see setter-based ViewChild) -->
@if (chartFullscreen) {
  <div
    class="chart-fullscreen-overlay"
    tabindex="-1"
    (click)="chartFullscreen = false"
    (keydown.escape)="chartFullscreen = false"
  >
    <div class="chart-fullscreen-panel" (click)="$event.stopPropagation()">
      <button
        #chartFullscreenCloseBtn
        class="chart-fullscreen-close"
        (click)="chartFullscreen = false"
        aria-label="Close fullscreen chart"
      >
        <svg lucideX></svg>
      </button>
      @if (recoveryPointAttemptsLoaded) {
        <rpo-chart [rpo]="application.rpo" [recoveryPointAttempts]="recoveryPointAttempts"></rpo-chart>
      }
    </div>
  </div>
}
